import { computed, ComputedRef, onBeforeUnmount, Ref, ref, watch } from 'vue'

import { InstagramInstantwinPretestEntryCode } from '@/typings/api/instagram_instantwin_pretest_entry_codes.schema'

const UPDATE_INTERVAL_MS = 60_000
const MS_PER_MINUTE = 60_000

interface UseInstagramDmTestCodeExpiryResult {
  remainingMinutes: ComputedRef<number>
  isExpired: ComputedRef<boolean>
}

export const useInstagramDmTestCodeExpiry = (
  current: Ref<InstagramInstantwinPretestEntryCode | null>,
  active: Ref<boolean>
): UseInstagramDmTestCodeExpiryResult => {
  const now = ref(Date.now())
  let timerId: ReturnType<typeof setInterval> | null = null

  const start = () => {
    if (timerId !== null) return
    now.value = Date.now()
    timerId = setInterval(() => {
      now.value = Date.now()
    }, UPDATE_INTERVAL_MS)
  }

  const stop = () => {
    if (timerId !== null) {
      clearInterval(timerId)
      timerId = null
    }
  }

  watch(
    active,
    value => {
      if (value) {
        start()
      } else {
        stop()
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(stop)

  const expiresAtMs = computed(() => {
    if (!current.value) return null
    const ms = new Date(current.value.expiresAt).getTime()
    return Number.isNaN(ms) ? null : ms
  })

  const remainingMinutes = computed(() => {
    if (!current.value) return 0
    if (expiresAtMs.value === null) {
      return Math.max(current.value.remainingMinutes, 0)
    }
    const ceiled = Math.max(0, Math.ceil((expiresAtMs.value - now.value) / MS_PER_MINUTE))
    return Math.min(current.value.expirationMinutes, ceiled)
  })

  const isExpired = computed(() => {
    if (!current.value) return false
    if (current.value.isExpired) return true
    if (expiresAtMs.value === null) return false
    return expiresAtMs.value <= now.value
  })

  return { remainingMinutes, isExpired }
}
